import * as React from "react";

import { Body1, Spinner, Title1 } from "@fluentui/react-components";
import { useNavigate } from "react-router-dom";

import { useStyles } from "./styles";
import ProgressBar from "../../../../components/ProgressBar/ProgressBar";
import { resetMatches } from "../../../../lib/scanner";
import { CitationStorageService } from "../../../../service/citationStorage.service";
import { useCitationStore } from "../../../../store/CitationStore";

const Cleaning: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { clearCitations } = useCitationStore();
  const [isLoading, setIsLoading] = React.useState(true);
  const [progress, setProgress] = React.useState(0);
  const [statusMessage, setStatusMessage] = React.useState("Initializing...");
  const [clearedCount, setClearedCount] = React.useState(0);

  React.useEffect(() => {
    const performCleaning = async () => {
      const timings: Record<string, number> = {};
      const totalStartTime = performance.now();

      const logTiming = (label: string, startTime: number) => {
        const duration = performance.now() - startTime;
        timings[label] = duration;
        console.log(`⏱️ [${label}] completed in ${duration.toFixed(2)}ms`);
      };

      try {
        setProgress(0.1);
        setIsLoading(true);
        setStatusMessage("Preparing to clear citations...");

        // ═══════════════════════════════════════════════════════════════════════
        // PARALLEL EXECUTION: XML storage + Word content controls run together
        // These are independent operations that can safely run in parallel
        // ═══════════════════════════════════════════════════════════════════════
        console.log("📍 [Steps 1-2] Running cleanup operations in PARALLEL...");
        const parallelStart = performance.now();
        setProgress(0.3);
        setStatusMessage("Clearing citation data and markers...");

        let clearedMarkersCount = 0;

        // Wrap promises to catch errors individually (Promise.allSettled alternative)
        const [xmlResult, markersResult] = await Promise.all([
          // Step 1: Clear citation data from XML storage
          (async () => {
            try {
              console.log("  📍 [Step 1] Clearing XML storage...");
              const step1Start = performance.now();
              await CitationStorageService.clearAllCitationResults();
              logTiming("Step 1: clearAllCitationResults (XML storage)", step1Start);
              return { status: "fulfilled", value: undefined };
            } catch (error) {
              console.error("Failed to clear XML storage:", error);
              return { status: "rejected", reason: error };
            }
          })(),

          // Step 2: Clear citation markers from document
          (async () => {
            try {
              console.log("  📍 [Step 2] Removing content controls...");
              const step2Start = performance.now();
              const count = await resetMatches();
              clearedMarkersCount = count;
              logTiming("Step 2: resetMatches (Word content controls)", step2Start);
              console.log(`  → Cleared ${count} citation markers`);
              return { status: "fulfilled", value: count };
            } catch (error) {
              console.error("Failed to clear markers:", error);
              return { status: "rejected", reason: error };
            }
          })(),
        ]);

        setClearedCount(clearedMarkersCount);
        logTiming("Steps 1-2 PARALLEL TOTAL", parallelStart);

        // Log any failures
        if (xmlResult.status === "rejected") {
          console.error("Failed to clear XML storage:", xmlResult.reason);
        }
        if (markersResult.status === "rejected") {
          console.error("Failed to clear markers:", markersResult.reason);
        }

        setProgress(0.8);

        // Step 3: Clear React context (must run after parallel operations)
        console.log("📍 [Step 3] Clearing React context...");
        const step3Start = performance.now();
        setStatusMessage("Finalizing...");
        clearCitations();
        logTiming("Step 3: clearCitations (React context)", step3Start);

        // Complete
        setProgress(1.0);
        setStatusMessage("Cleaning complete!");
      } catch (error) {
        console.error("Cleaning failed:", error);
        setStatusMessage("An error occurred during cleaning");
        setProgress(1.0);
      } finally {
        setIsLoading(false);

        // Print timing summary
        const totalDuration = performance.now() - totalStartTime;
        console.log("\n📊 ═══════════════════════════════════════════════════");
        console.log("📊 CLEANING PERFORMANCE SUMMARY");
        console.log("📊 ═══════════════════════════════════════════════════");
        Object.entries(timings).forEach(([label, duration]) => {
          const percentage = ((duration / totalDuration) * 100).toFixed(1);
          const bar = "█".repeat(Math.round(Number(percentage) / 5));
          console.log(
            `📊 ${bar.padEnd(20)} ${percentage.padStart(5)}% | ${duration.toFixed(0).padStart(6)}ms | ${label}`
          );
        });
        console.log("📊 ───────────────────────────────────────────────────");
        console.log(
          `📊 TOTAL TIME: ${totalDuration.toFixed(2)}ms (${(totalDuration / 1000).toFixed(2)}s)`
        );
        console.log("📊 ═══════════════════════════════════════════════════\n");
      }
    };

    performCleaning();
  }, [clearCitations]);

  // Navigate to home after cleaning completes
  React.useEffect(() => {
    if (!isLoading && progress === 1.0) {
      const timer = setTimeout(() => {
        // Pass state to indicate we just cleaned - Home should NOT check storage
        navigate("/", { state: { fromCleaning: true } });
      }, 1000); // Wait 1 second

      return () => clearTimeout(timer);
    }

    return () => {}; // Ensure we always return a cleanup function
  }, [isLoading, progress, navigate]);

  return (
    <div className={styles.container}>
      <Title1>Cleaning</Title1>
      <Body1>
        {isLoading ? "Removing all citations from your document..." : "Cleaning complete!"}
      </Body1>

      <div className={styles.progressSection}>
        <Body1>
          Progress: {Math.round(progress * 100)}% - {statusMessage}
        </Body1>
        <ProgressBar value={progress} loading={isLoading} />
      </div>

      {!isLoading && (
        <Body1>
          Successfully removed {clearedCount} citation{clearedCount !== 1 ? "s" : ""} from the
          document.
        </Body1>
      )}

      {isLoading && <Spinner size="tiny" />}
    </div>
  );
};

export default Cleaning;
